import React from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Icon,
  alpha,
  useTheme,
} from "@mui/material";
import { AppTable } from "../../../../../components/AppTable";
import { VoucherTableProps } from "../types";
import { IVoucherListItem } from "../../../accounting.interface";
import { getPaymentMethodLabel } from "../utils/voucherUtils";
import { IdDisplay } from "../../../components/IdDisplay";
import { DateDisplay } from "../../../components/DateDisplay";

export const VoucherTable: React.FC<VoucherTableProps> = ({
  data,
  pagination,
  onVoucherClick,
  onReverseVoucher,
  onDeleteVoucher,
  onEditVoucher,
  onPostVoucher,
  onCopyVoucherId,
  onDownloadPDF,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedVoucher, setSelectedVoucher] =
    React.useState<IVoucherListItem | null>(null);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    voucher: IVoucherListItem
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedVoucher(voucher);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedVoucher(null);
  };

  const handleMenuAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  const columns = [
    {
      id: "id",
      header: "Voucher ID",
      field: "id",
      type: "action" as const,
      width: "min-content",
      renderAction: (row: any) => (
        <IdDisplay
          id={row.id}
          onClick={() => onVoucherClick(row)}
          onCopy={onCopyVoucherId}
          label="Copy Voucher ID"
        />
      ),
    },
    {
      id: "formattedDate",
      header: "Date",
      field: "formattedDate",
      type: "action" as const,
      width: "100px",
      renderAction: (row: any) => (
        <DateDisplay timestamp={row.voucher_date} showTooltip={true} />
      ),
    },
    {
      id: "voucher_type",
      header: "Type",
      field: "formattedVoucherType",
      type: "text" as const,
      width: "140px",
    },
    {
      id: "beneficiary",
      header: "Beneficiary",
      field: "beneficiary",
      type: "text" as const,
      width: "200px",
      accessor: (row: any) => row.beneficiary || "—",
    },
    {
      id: "linked_entity",
      header: "Linked Entity",
      field: "linked_entity",
      type: "text" as const,
      width: "140px",
      accessor: (row: any) => row.linked_entity || "—",
    },
    {
      id: "payment_method",
      header: "Payment Method",
      field: "payment_method",
      type: "text" as const,
      width: "120px",
      accessor: (row: any) => getPaymentMethodLabel(row.payment_method),
    },
    {
      id: "formattedAmount",
      header: "Amount",
      field: "formattedAmount",
      type: "text" as const,
      width: "120px",
      align: "right" as const,
    },
    {
      id: "branchName",
      header: "Branch",
      field: "branchName",
      type: "text" as const,
      width: "140px",
    },
    {
      id: "status",
      header: "Status",
      field: "formattedStatus",
      type: "text" as const,
      width: "100px",
    },
    {
      id: "posted_by",
      header: "Posted By",
      field: "posted_by",
      type: "text" as const,
      width: "120px",
    },
    {
      id: "actions",
      header: "Actions",
      field: "actions",
      type: "action" as const,
      width: "80px",
      align: "center" as const,
      renderAction: (row: any) => (
        <Tooltip title="More actions">
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, row)}
            sx={{
              color: "text.secondary",
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
              },
            }}
          >
            <Icon>more_vert</Icon>
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <AppTable
        data={data}
        columns={columns}
        pagination={pagination}
        onRowClick={(row) => onVoucherClick(row as any)}
        emptyState={{
          title: "No vouchers found",
          description: "Try adjusting your filters or create a new voucher",
          icon: "receipt",
        }}
      />

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 200,
            boxShadow: theme.shadows[8],
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() =>
            handleMenuAction(() => onVoucherClick(selectedVoucher!))
          }
        >
          <ListItemIcon>
            <Icon>visibility</Icon>
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>

        {selectedVoucher?.status === "draft" && (
          <MenuItem
            onClick={() =>
              handleMenuAction(() => onEditVoucher(selectedVoucher!))
            }
          >
            <ListItemIcon>
              <Icon>edit</Icon>
            </ListItemIcon>
            <ListItemText>Edit Voucher</ListItemText>
          </MenuItem>
        )}

        {selectedVoucher?.status === "draft" && (
          <MenuItem
            onClick={() =>
              handleMenuAction(() => onPostVoucher(selectedVoucher!))
            }
          >
            <ListItemIcon>
              <Icon>check_circle</Icon>
            </ListItemIcon>
            <ListItemText>Post Voucher</ListItemText>
          </MenuItem>
        )}

        {selectedVoucher?.status === "posted" && (
          <MenuItem
            onClick={() =>
              handleMenuAction(() => onReverseVoucher(selectedVoucher!))
            }
          >
            <ListItemIcon>
              <Icon>undo</Icon>
            </ListItemIcon>
            <ListItemText>Reverse Voucher</ListItemText>
          </MenuItem>
        )}

        <MenuItem
          onClick={() =>
            handleMenuAction(() => onCopyVoucherId(selectedVoucher!.voucher_id))
          }
        >
          <ListItemIcon>
            <Icon>content_copy</Icon>
          </ListItemIcon>
          <ListItemText>Copy Voucher ID</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() =>
            handleMenuAction(() => onDownloadPDF(selectedVoucher!))
          }
        >
          <ListItemIcon>
            <Icon>download</Icon>
          </ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>

        {selectedVoucher?.status === "draft" && (
          <>
            <MenuItem
              onClick={() =>
                handleMenuAction(() => onDeleteVoucher(selectedVoucher!))
              }
              sx={{ color: "error.main" }}
            >
              <ListItemIcon>
                <Icon sx={{ color: "error.main" }}>delete</Icon>
              </ListItemIcon>
              <ListItemText>Delete Voucher</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};
